import React, { useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { PageHeader } from "@ant-design/pro-components";
import {
    Layout,
    Breadcrumb,
    Button,
    ConfigProvider,
    Card,
    Tooltip,
    Drawer,
    Tag,
    Space,
    Descriptions,
    Divider,
} from 'antd';
import { ProTable, ProCard } from '@ant-design/pro-components';
import {
    CheckCircleTwoTone,
    CloseCircleTwoTone,
    ExclamationCircleTwoTone,
    PauseCircleTwoTone,
    ClockCircleTwoTone,
} from '@ant-design/icons';
import TopBar from 'components/NavBar/TopBar';
import DrawerSider from 'components/NavBar/DrawerSider';
import { LayoutTopSide } from 'components/NavBar/styles';
import ptBRIntl from 'antd/lib/locale/pt_BR';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'reactjs-popup/dist/index.css';
import { FileSearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { defaultTablePagination } from 'utils/table-pagination';


import { getAutomationsHealth } from '../../../auth/actions/automationsActions';
import { LogViewer } from '../Edit/Flow/Log/LogViewer';
import { usePersistentFilters } from 'utils/use-persistents-filters';
import { useLocation } from "react-router-dom";

moment.locale('pt-br');

const ICONS = {
    healthy: <CheckCircleTwoTone twoToneColor="#52c41a" />,
    unstable: <ExclamationCircleTwoTone twoToneColor="#faad14" />,
    broken: <CloseCircleTwoTone twoToneColor="#f5222d" />,
    paused: <PauseCircleTwoTone twoToneColor="#595959" />,
    none: <ClockCircleTwoTone twoToneColor="#d9d9d9" />,
};

function getHealth(automation) {
    if (!automation.lastLogs || automation.lastLogs.length === 0) return { status: 'none', label: 'Sem dados' };

    const fails = automation.lastLogs.filter((log) => !log.success).length;
    if (fails === 0) return { status: 'healthy', label: 'Saudável' };
    if (fails <= 2) return { status: 'unstable', label: 'Instável' };
    return { status: 'broken', label: 'Quebrada' };
}

const namePage = "automation-dashboard";
const filterConfig = [
    { field: 'name', queryParam: `${namePage}-name`, localKey: `${namePage}-name`, type: 'text' },
    { field: 'healthMin', queryParam: `${namePage}-healthMin`, localKey: `${namePage}-healthMin`, type: 'text' },
    { field: 'lastErrorDate', type: 'dateRange', queryParamStart: `${namePage}-lastErrorDateStart`, queryParamEnd: `${namePage}-lastErrorDateEnd`, localKeyStart: `${namePage}-lastErrorDateStart`, localKeyEnd: `${namePage}-lastErrorDateEnd` },
    { field: 'active', queryParam: `${namePage}-active`, localKey: `${namePage}-active`, type: 'text' },
]


function DashboardAutomation({ user }) {
    const company = useSelector((state) => state.user.company);
    const navigate = useNavigate();
    const [selected, setSelected] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedLogId, setSelectedLogId] = useState(null);
    const [errorDrawerOpen, setErrorDrawerOpen] = useState(false);
    const [errorLogId, setErrorLogId] = useState(null);

    const actionRef = useRef();
    const formRef = useRef();
    const location = useLocation();

    function handleViewError(record) {
        if (record.lastErrorLogId) {
            setErrorLogId(record.lastErrorLogId);
            setErrorDrawerOpen(true);
        }
    }
    const routes = [
        { breadcrumbName: 'Automações', href: '/automacoes' },
        { breadcrumbName: 'Dashboard de Execução' },
    ];

    const { initialValues, onSubmitFilters, onReset } = usePersistentFilters(filterConfig, actionRef, formRef, location, navigate);


    const columns = [
        {
            title: '',
            dataIndex: 'health',
            align: 'center',
            width: 48,
            search: false,
            render: (_, record) => {
                const health = getHealth(record);
                return <Tooltip title={health.label}>{ICONS[health.status]}</Tooltip>;
            },
        },
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
            width: 320,
            valueType: 'text',
            ellipsis: true,
            fieldProps(form, config) {
                return { ...config, placeholder: '' };
            },
            render: (dom, record) => (
                <Tooltip title={record.description || record.name}>
                    <a
                        href="#"
                        style={{ color: '#0e9c84' }}
                        onClick={() => navigate(`/automacoes/editar/${record._id}`)}
                    >
                        {record.name?.replace('obj_', '')}
                    </a>
                </Tooltip>
            ),
        },
        {
            title: 'Saúde',
            dataIndex: 'healthMin',
            key: 'healthMin',
            hideInTable: true,
            valueType: 'select',
            valueEnum: {
                null: { text: 'Todas' },
                100: { text: 'Saudável (100%)' },
                60: { text: 'Instável (>=60%)' },
                1: { text: 'Quebrada (<60%)' },
            },
            fieldProps(form, config) {
                return { ...config, placeholder: '' };
            },
        },
        {
            title: 'Execuções',
            dataIndex: 'lastLogs',
            key: 'lastLogs',
            align: 'center',
            width: 140,
            search: false,
            responsive: ['md'],
            render: (_, record) => {
                if (!record.lastLogs || record.lastLogs.length === 0) {
                    return <Tooltip title="Sem execuções">{ICONS.none}</Tooltip>;
                }
                return (
                    <Space size={4}>
                        {record.lastLogs.map((log, idx) => (
                            <Tooltip
                                key={log._id || idx}
                                title={`Execução ${idx + 1} - ${log.success ? 'Sucesso' : 'Falha'} - ${moment(log.startDate).fromNow()}`}
                            >
                                {log.success ? ICONS.healthy : ICONS.broken}
                            </Tooltip>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Data do Último Erro',
            dataIndex: 'lastErrorDate',
            key: 'lastErrorDate',
            width: 180,
            valueType: 'dateRange',
            responsive: ['lg'],
            fieldProps(form, config) {
                return {
                    ...config,
                    format: 'DD/MM/YYYY',
                    placeholder: ['Data inicial', 'Data final'],
                    allowClear: true,
                };
            },
            render: (_, record) =>
                record.lastErrorDate && moment(record.lastErrorDate).isValid() ? (
                    <Tooltip title={moment(record.lastErrorDate).fromNow()}>
                        <Tag color="red">{moment(record.lastErrorDate).format('DD/MM/YYYY HH:mm')}</Tag>
                    </Tooltip>
                ) : (
                    (<></>) // vazio quando não há erro
                ),
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            width: 100,
            valueType: 'select',
            valueEnum: {
                null: 'Todos',
                true: 'Ativo',
                false: 'Inativo',
            },
            fieldProps(form, config) {
                return { ...config, placeholder: '' };
            },
            render: (_, record) => (record.active ? <Tag color="green">Ativo</Tag> : <Tag>Inativo</Tag>),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center',
            width: 280,
            valueType: 'option',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<FileSearchOutlined />}
                        onClick={() => navigate(`/automacoes/editar/${record._id}/logs`)}
                    >
                        Logs
                    </Button>
                    <Button
                        size="small"
                        type="default"
                        icon={<InfoCircleOutlined />}
                        onClick={() => {
                            setSelected(record);
                            setDetailsOpen(true);
                        }}
                    >
                        Detalhes
                    </Button>
                    {record.lastErrorLogId && (
                        <Tooltip title="Ver detalhes do último erro">
                            <Button
                                size="small"
                                type="primary"
                                danger
                                icon={<InfoCircleOutlined />}
                                onClick={() => handleViewError(record)}
                            >
                                Erro
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];


    return (
        (<Layout>
            <LayoutTopSide>
                <TopBar />
                <Layout style={{ overflow: "hidden" }}>
                    <DrawerSider />
                    <Layout style={{ padding: '0 24px 24px', height: '100%', overflowY: 'auto' }}>
                        <PageHeader
                            style={{ margin: '16px 0 5px 0' }}
                            title="Dashboard de execução"
                            subTitle="Acompanhe o status e o histórico das execuções das suas automações."
                            onBack={() => window.history.back()}
                            breadcrumb={
                                <Breadcrumb>
                                    {routes.map((a, i) =>
                                        a.href ? (
                                            <Breadcrumb.Item key={i}>
                                                <a onClick={() => navigate(a.href)}>{a.breadcrumbName}</a>
                                            </Breadcrumb.Item>
                                        ) : (
                                            <Breadcrumb.Item key={i}>{a.breadcrumbName}</Breadcrumb.Item>
                                        )
                                    )}
                                </Breadcrumb>
                            }
                        />
                        <Card>
                            <ConfigProvider locale={ptBRIntl}>
                                <ProTable
                                    rowKey="_id"
                                    type="table"
                                    search={{ labelWidth: 'auto' }}
                                    pagination={defaultTablePagination}
                                    params={initialValues}
                                    formRef={formRef}
                                    actionRef={actionRef}
                                    onSubmit={onSubmitFilters}
                                    onReset={onReset}
                                    request={async (params = {}) => {
                                        const {
                                            active = null,
                                            name,
                                            healthMin,
                                            lastErrorDate,
                                            current = 1,
                                            pageSize = 10,
                                        } = params;

                                        const query = {
                                            unitId: company.unitId,
                                            page: current,
                                            perPage: pageSize,
                                        };

                                        if (active !== null && active !== undefined) query.active = active === 'true';
                                        if (name) query.name = name;
                                        if (healthMin !== null && healthMin !== undefined) query.healthMin = Number(healthMin);
                                        if (Array.isArray(lastErrorDate)) {
                                            const [start, end] = lastErrorDate;
                                            const startMoment = start != null ? moment(Number(start) || start) : null;
                                            const endMoment = end != null ? moment(Number(end) || end) : null;
                                            if (startMoment?.isValid()) query.lastErrorDateStart = startMoment.startOf('day').toISOString();
                                            if (endMoment?.isValid()) query.lastErrorDateEnd = endMoment.endOf('day').toISOString();
                                        }

                                        const response = await getAutomationsHealth(query);

                                        return {
                                            data: response.data,
                                            success: true,
                                            total: response.total,
                                        };
                                    }}
                                    columns={columns}
                                    dateFormatter="number"
                                    scroll={{ x: true }}
                                    style={{ padding: 0 }}
                                />
                            </ConfigProvider>
                        </Card>

                        <Drawer
                            placement="bottom"
                            height="60%"
                            destroyOnHidden
                            open={detailsOpen}
                            title={selected ? `Detalhes da automação: ${selected.name}` : 'Detalhes da automação'}
                            onClose={() => setDetailsOpen(false)}
                            afterOpenChange={(open) => {
                                if (!open) {
                                    setSelected(null);
                                    setSelectedLogId(null);
                                }
                            }}
                        >
                            {selected && (
                                <ProCard split="vertical">
                                    <ProCard colSpan="400px">
                                        <Descriptions layout="vertical" size="small" colon={false}>
                                            <Descriptions.Item
                                                label="Status">{getHealth(selected).label}</Descriptions.Item>
                                            <Descriptions.Item label="Última execução">
                                                {selected.lastLogs?.[0]?.startDate ? moment(selected.lastLogs[0].startDate).format('DD/MM/YYYY HH:mm:ss') : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Duração média">
                                                {selected.averageExecutionTime ? `${(selected.averageExecutionTime / 1000).toFixed(1)}s` : '-'}
                                            </Descriptions.Item>
                                        </Descriptions>

                                        <Divider />

                                        <div style={{ marginBottom: 8 }}><strong>Últimas execuções:</strong></div>
                                        <Space wrap>
                                            {selected.lastLogs?.map((log, idx) => (
                                                <Tag
                                                    key={log._id || idx}
                                                    icon={log.success ? <CheckCircleTwoTone /> : <CloseCircleTwoTone />}
                                                    color={log.success ? 'success' : 'error'}
                                                    onClick={() => setSelectedLogId(log._id)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {moment(log.startDate).fromNow()}
                                                </Tag>
                                            ))}
                                        </Space>
                                    </ProCard>
                                    <ProCard>
                                        {selectedLogId ? <LogViewer logId={selectedLogId} /> :
                                            <p>Selecione uma execução para ver os logs.</p>}
                                    </ProCard>
                                </ProCard>
                            )}
                        </Drawer>
                        <Drawer
                            size="large"
                            placement="right"
                            destroyOnHidden
                            open={errorDrawerOpen}
                            title="Detalhes do erro"
                            onClose={() => {
                                setErrorDrawerOpen(false);
                                setErrorLogId(null);
                            }}
                        >
                            {errorLogId ? (
                                <LogViewer logId={errorLogId} />
                            ) : (
                                <p>Nenhum log selecionado.</p>
                            )}
                        </Drawer>

                    </Layout>
                </Layout>
            </LayoutTopSide>
        </Layout>)
    );
}

const mapStateToProps = ({ session }) => ({ user: session.user });

export default connect(mapStateToProps)(DashboardAutomation);
